// Organizations API - matching admin-web structure
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import {
  IOrganization,
  ICreateOrganization,
  IOrganizationListResponse,
  IOrganizationDetailsResponse,
  IOrganizationApiResponse,
} from '@/types/admin/organizations';

export interface GetOrganizationArgs {
  organizationId: number;
  includePropertyDetails?: boolean;
}

// RTK Query API slice for organizations (matching admin-web pattern)
export const organizationsApi = createApi({
  reducerPath: 'organizationsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Organizations', 'OrganizationDetails', 'OrganizationAdmins', 'OrganizationProperties'],
  endpoints: (builder) => ({
    // List all organizations
    listOrganizations: builder.query<IOrganizationListResponse, void>({
      query: () => ({
        url: '/api/v1/organizations/',
        method: 'GET',
      }),
      providesTags: ['Organizations'],
    }),

    // Get single organization with optional property details
    getOrganization: builder.query<IOrganizationDetailsResponse, GetOrganizationArgs>({
      query: ({ organizationId, includePropertyDetails = false }) => ({
        url: `/api/v1/organizations/${organizationId}?includePropertyDetails=${includePropertyDetails}`,
        method: 'GET',
      }),
      providesTags: (result, error, { organizationId }) => [
        { type: 'OrganizationDetails', id: organizationId },
      ],
    }),

    // Create new organization
    postOrganization: builder.mutation<IOrganizationApiResponse, ICreateOrganization>({
      query: (organization) => ({
        url: '/api/v1/organizations',
        method: 'POST',
        body: organization,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Organizations'],
    }),

    // Update existing organization
    putOrganization: builder.mutation<IOrganizationApiResponse, IOrganization>({
      query: (organization) => ({
        url: `/api/v1/organizations/${organization.id}`,
        method: 'PUT',
        body: organization,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, organization) => [
        'Organizations',
        { type: 'OrganizationDetails', id: organization.id },
      ],
    }),

    // Delete organization
    deleteOrganization: builder.mutation<IOrganizationApiResponse, number>({
      query: (organizationId) => ({
        url: `/api/v1/organizations/${organizationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organizations'],
    }),

    // Get organization admins
    getOrganizationAdmins: builder.query<IOrganizationApiResponse, number>({
      query: (organizationId) => ({
        url: `/api/v1/organizations/${organizationId}/admins`,
        method: 'GET',
      }),
      providesTags: (result, error, organizationId) => [
        { type: 'OrganizationAdmins', id: organizationId },
      ],
    }),

    // Get organization properties
    getOrganizationProperties: builder.query<IOrganizationApiResponse, number>({
      query: (organizationId) => ({
        url: `/api/v1/organizations/${organizationId}/properties`,
        method: 'GET',
      }),
      providesTags: (result, error, organizationId) => [
        { type: 'OrganizationProperties', id: organizationId },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useListOrganizationsQuery,
  useLazyListOrganizationsQuery,
  useGetOrganizationQuery,
  useLazyGetOrganizationQuery,
  usePostOrganizationMutation,
  usePutOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationAdminsQuery,
  useLazyGetOrganizationAdminsQuery,
  useGetOrganizationPropertiesQuery,
  useLazyGetOrganizationPropertiesQuery,
} = organizationsApi;

